import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FiRefreshCw, FiInfo } from 'react-icons/fi';
import { versionService } from '../../services/versionService';

interface VersionUpdateModalProps {
  show: boolean;
  onHide: () => void;
  onUpdate: () => void;
}

const VersionUpdateModal: React.FC<VersionUpdateModalProps> = ({ show, onHide, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = () => {
    setIsUpdating(true);
    onUpdate();
    // The page will reload, so no need to set isUpdating to false
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center">
          <FiRefreshCw className="me-2 text-primary" />
          Update Available
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="d-flex align-items-start">
          <FiInfo className="me-2 mt-1 flex-shrink-0" />
          <div>
            <strong>A new version of Smart Counsellor is available!</strong>
            <p className="mt-2 mb-0">
              To ensure you have the latest features and security updates, 
              please refresh the application.
            </p>
          </div>
        </Alert>
        <p className="mb-0">
          <small className="text-muted">
            This update includes important improvements and bug fixes.
          </small>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={isUpdating}
        >
          Later
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpdate}
          disabled={isUpdating}
          className="d-flex align-items-center"
        >
          {isUpdating ? (
            <>
              <span 
                className="spinner-border spinner-border-sm me-2" 
                role="status" 
                aria-hidden="true"
              ></span>
              Updating...
            </>
          ) : (
            <>
              <FiRefreshCw className="me-2" />
              Update Now
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Hook to manage version updates
 */
export const useVersionUpdate = () => {
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    // Only do an initial check when the app loads, no periodic checking
    const checkInitialVersion = async () => {
      const hasUpdate = await versionService.checkForNewVersion();
      if (hasUpdate) {
        setShowUpdateModal(true);
      }
    };

    // Check once on mount
    checkInitialVersion();

    // No periodic checking - commenting out the automatic version checking
    // versionService.startVersionChecking(() => {
    //   setShowUpdateModal(true);
    // });

    // Cleanup on unmount (though there's nothing to cleanup now)
    return () => {
      versionService.stopVersionChecking();
    };
  }, []);

  const handleUpdate = () => {
    versionService.forceRefresh();
  };

  const handleDismiss = () => {
    setShowUpdateModal(false);
  };

  return {
    showUpdateModal,
    handleUpdate,
    handleDismiss,
  };
};

export default VersionUpdateModal;
